/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// ดาวน์โหลดภาพผลงาน — ทีละภาพ หรือหลายภาพรวมเป็น zip เดียว (รอบ 2 ข้อ A2 · 25 ต.ค. 2569)
// ใช้ร่วม 4 หน้า: ภาพสินค้า AI · กราฟิกแคมเปญ · จัดการภาพ · คิวงานภาพ
// โหลดรูป 3 ทาง (รีเช็ค 26 ต.ค. 2569): ① ตรงจาก URL ② path เดียวกันบนโดเมนหน้าเว็บ (dev: CRA proxy ส่งต่อ 1995 → 8000)
// ③ ผ่าน /api/image_export/file — บนเครื่องจริงรูปเสิร์ฟจากโดเมนหลัก (services.php ตัด subdomain) ส่วนหน้าเว็บอยู่ subdomain
//    ถ้าโดเมนรูปไม่ส่ง CORS ① ล้ม และ ② ได้ index.html (htaccess ส่งหน้าแอปแทนไฟล์ที่ไม่มี) จึงต้องเช็คว่าได้ "รูป" จริง
import { FetchData, currentToken } from 'services/Request';
import zipStore from './zipStore';

const relativeOf = (url) => {
  try {
    const u = new URL(url, window.location.href);
    return u.pathname + u.search;
  } catch (e) {
    return url;
  }
};

/* ได้ไฟล์รูปจริงเท่านั้น — กันหน้า index.html (200 text/html) ถูกเก็บเป็น .jpg */
const fetchImage = async (u) => {
  const res = await fetch(u, { credentials: 'omit' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  if (!/^image\//i.test(blob.type || '')) throw new Error(`not image ${blob.type}`);
  return { bytes: new Uint8Array(await blob.arrayBuffer()), type: blob.type };
};

const viaApi = async (url) => {
  const path = relativeOf(url).replace(/[?#].*$/, '');
  const r = await FetchData('OPTIONS', '/image_export/file', currentToken(), {
    path,
    _t: Date.now(),
  });
  if (r.status !== 'ok' || !r.data) throw new Error(r.msg || 'file_fail');
  const bin = atob(r.data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return { bytes, type: r.mime || '' };
};

export const fetchImageBytes = async (url) => {
  try {
    return await fetchImage(url);
  } catch (e) {
    /* ลองทางถัดไป */
  }
  const rel = relativeOf(url);
  if (rel !== url) {
    try {
      return await fetchImage(rel);
    } catch (e) {
      /* ลองทางถัดไป */
    }
  }
  return viaApi(url);
};

/* นามสกุลจาก URL ก่อน ไม่มีค่อยดูชนิดไฟล์ที่ได้ */
export const extOf = (url, type) => {
  const m = /\.(png|jpe?g|webp)(\?|#|$)/i.exec(url || '');
  if (m) return m[1].toLowerCase() === 'jpeg' ? 'jpg' : m[1].toLowerCase();
  if (/png/i.test(type)) return 'png';
  if (/webp/i.test(type)) return 'webp';
  return 'jpg';
};

/* ตัดอักขระที่ใช้ตั้งชื่อไฟล์ไม่ได้ (Windows/macOS) */
export const safeName = (s) =>
  String(s || '')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();

/* โฟลเดอร์ซ้อนใน zip เช่น รหัสแคมเปญ/รหัสสินค้า — ล้างชื่อทีละชั้น (หน้าดาวน์โหลดรูป · 25 ต.ค. 2569) */
export const safeFolder = (folder) =>
  String(folder || '')
    .split('/')
    .map(safeName)
    .filter(Boolean)
    .join('/');

export const saveBlob = (blob, name) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
};

/** ดาวน์โหลดภาพเดียว · baseName ไม่ต้องใส่นามสกุล */
export async function downloadImage(url, baseName) {
  const { bytes, type } = await fetchImageBytes(url);
  saveBlob(
    new Blob([bytes], { type: type || 'image/png' }),
    `${safeName(baseName)}.${extOf(url, type)}`
  );
}

/**
 * ดาวน์โหลดหลายภาพเป็น zip เดียว
 * items = [{ url, name, folder? }] · name ไม่ต้องใส่นามสกุล · folder = โฟลเดอร์ย่อยใน zip (เช่น รหัสสินค้า)
 * onProgress(done, total) เรียกทุกภาพที่โหลดเสร็จ
 * คืน { count, skipped: [ชื่อ] } — ไฟล์ที่โหลดไม่ได้ข้ามไป ไม่ทิ้งทั้ง zip (รีเช็ค 26 ต.ค.) · โหลดไม่ได้เลยสักไฟล์ = throw
 */
export async function downloadZip(items, zipName, onProgress) {
  const files = [];
  const skipped = [];
  const used = new Set();
  for (let i = 0; i < items.length; i += 1) {
    const it = items[i];
    let got = null;
    try {
      // eslint-disable-next-line no-await-in-loop
      got = await fetchImageBytes(it.url);
    } catch (e) {
      skipped.push((it.folder ? `${it.folder}/` : '') + it.name);
    }
    if (onProgress) onProgress(i + 1, items.length);
    // eslint-disable-next-line no-continue
    if (!got) continue;
    const { bytes, type } = got;
    const ext = extOf(it.url, type);
    const base = (it.folder ? `${safeFolder(it.folder)}/` : '') + safeName(it.name);
    let name = `${base}.${ext}`;
    let n = 2;
    while (used.has(name)) {
      name = `${base} (${n}).${ext}`;
      n += 1;
    }
    used.add(name);
    files.push({ name, data: bytes });
  }
  if (!files.length) throw new Error('nothing_downloaded');
  saveBlob(zipStore(files), `${safeName(zipName)}.zip`);
  return { count: files.length, skipped };
}

/* ข้อความสรุปหลังโหลด zip — ใช้ร่วมทุกหน้า */
export const zipDoneText = (res, unit = 'ภาพ') =>
  res.skipped.length
    ? `ดาวน์โหลด ${res.count} ${unit} · ข้าม ${res.skipped.length} ไฟล์ที่โหลดไม่ได้: ${res.skipped
        .slice(0, 3)
        .join(', ')}${res.skipped.length > 3 ? ' …' : ''}`
    : `ดาวน์โหลด ${res.count} ${unit}แล้ว`;
